import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  TextButton,
  Typography,
  MenuButton,
  MenuItem,
  Divider,
  useMediaQuery,
  classes,
} from 'bonfire-kindling';

import { AppContext } from 'contexts';

const NavBarPrimary = ({ showSubscribedTabs }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const isTabletOrDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { isSubscribed, isOnboarded, isLoggedIn } = useContext(AppContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      {isMobile && (
        <>
          {showSubscribedTabs && !pathname.startsWith('/opportunities') && (
            <TextButton
              label={t('nav.opportunities')}
              to="/opportunities"
              component={RouterLink}
              variant="text"
              sx={{ mr: 2, fontWeight: 'bold' }}
            />
          )}
          <MenuButton
            onClick={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
            open={isMenuOpen}
            TextButtonProps={{
              'aria-label': t('nav.open_menu'),
              iconLeft: 'user',
              sx: { mr: 4 },
            }}
            MenuProps={{
              sx: {
                [`& .${classes.components.MuiMenuItem.root}`]: {
                  minHeight: 'auto',
                },
              },
            }}
          >
            {showSubscribedTabs && (
              <>
                <MenuItem label={t('nav.dashboard')} to="/dashboard" component={RouterLink} />
                <MenuItem label={t('nav.settings')} to="/settings" component={RouterLink} />
              </>
            )}
            {isSubscribed && (
              <MenuItem label={t('nav.support')} to="/support" exact component={RouterLink} />
            )}
            {isLoggedIn ? (
              <>
                <Divider />
                <MenuItem label={t('nav.log_out')} to="/logout" component={RouterLink} />
              </>
            ) : (
              <>
                <MenuItem
                  label={t('nav.support')}
                  href="https://gobonfire.com/support"
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                />
                <Divider />
                <MenuItem label={t('nav.log_in')} component={RouterLink} to="/login" />
              </>
            )}
          </MenuButton>
        </>
      )}
      {isTabletOrDesktop && (
        <>
          {isSubscribed && (
            <TextButton
              to="/support"
              component={RouterLink}
              label={t('nav.support')}
              exact
              target={isOnboarded ? '_self' : '_blank'}
              variant="outlined"
              sx={{ mr: 2 }}
            />
          )}
          {isLoggedIn ? (
            <TextButton
              component={RouterLink}
              to="/logout"
              label={t('nav.log_out')}
              variant="contained"
              sx={{ mr: 2 }}
            />
          ) : (
            <>
              <Typography
                color="textPrimary"
                variant="body2"
                sx={{ fontWeight: 'bold', ml: 2, display: { xs: 'none', md: 'block' } }}
              >
                {t('nav.are_you_premium')}
              </Typography>
              <TextButton
                component="a"
                href="https://gobonfire.com/support"
                target="_blank"
                rel="noopener noreferrer"
                label={t('nav.support')}
                variant="text"
              />
              <TextButton
                component={RouterLink}
                to="/login"
                variant="contained"
                color="primary"
                label={t('nav.log_in')}
                sx={{ px: 1, mx: 3 }}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

const NavBarPreview = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isTabletOrDesktop = useMediaQuery(theme => theme.breakpoints.up('sm'));
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      {isMobile && (
        <MenuButton
          onClick={() => setIsMenuOpen(true)}
          onClose={() => setIsMenuOpen(false)}
          open={isMenuOpen}
          TextButtonProps={{
            'aria-label': t('nav.open_menu'),
            iconLeft: 'user',
            sx: { mr: 4 },
          }}
          MenuProps={{
            sx: {
              [`& .${classes.components.MuiMenuItem.root}`]: {
                minHeight: 'auto',
              },
            },
          }}
        >
          <MenuItem
            label={t('nav.support')}
            href="https://gobonfire.com/support"
            component="a"
            target="_blank"
            rel="noopener noreferrer"
          />
          <Divider />
          <MenuItem label={t('nav.log_in')} to="/login" exact component={RouterLink} />
          <MenuItem label={t('nav.sign_up')} to="/subscribe" exact component={RouterLink} />
        </MenuButton>
      )}
      {isTabletOrDesktop && (
        <>
          <TextButton
            component="a"
            href="https://gobonfire.com/support"
            target="_blank"
            rel="noopener noreferrer"
            label={t('nav.support')}
            variant="text"
          />
          <TextButton
            component={RouterLink}
            to="/login"
            variant="outlined"
            color="primary"
            label={t('nav.log_in')}
            sx={{ px: 1 }}
          />
          <TextButton
            component={RouterLink}
            to="/subscribe"
            variant="contained"
            label={t('nav.sign_up')}
            sx={{ px: 1, mx: 3 }}
          />
        </>
      )}
    </>
  );
};

const NavBarRightContent = ({ variant, showSubscribedTabs }) => (
  <>
    {variant === 'primary' && <NavBarPrimary showSubscribedTabs={showSubscribedTabs} />}
    {variant === 'preview' && <NavBarPreview />}
  </>
);

export default NavBarRightContent;
